import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 泛知识与生活百科 - 竖屏「流程图指南」风格
 *
 * 核心亮点：
 * - SVG 节点连线动画（路径描边生长）
 * - 节点弹入 + 脉冲光环
 * - 阶段切换高亮
 * - 分支决策节点（菱形）
 * - 动态进度指示
 *
 * 尺寸：1080x1920 竖屏
 */

interface FlowNode {
  icon: string;
  label: string;
  detail: string;
  type: 'step' | 'decision' | 'result';
}

interface LifeProcessFlowProps {
  topic: string;
  subtitle: string;
  nodes: FlowNode[];
  conclusion: string;
  accentColor?: string;
}

const DEFAULT_PROPS: LifeProcessFlowProps = {
  topic: '如何建立一个有效的晨间习惯',
  subtitle: 'Morning Routine Framework',
  nodes: [
    { icon: '⏰', label: '固定时间起床', detail: '即使周末也不超过 30 分钟差异', type: 'step' },
    { icon: '💧', label: '起床先喝水', detail: '一杯温水唤醒身体代谢', type: 'step' },
    { icon: '🤔', label: '今天精力如何？', detail: '', type: 'decision' },
    { icon: '🏃', label: '高精力 → 运动 20min', detail: '跑步、HIIT 或力量训练', type: 'step' },
    { icon: '🧘', label: '低精力 → 冥想 10min', detail: '深呼吸 + 正念冥想', type: 'step' },
    { icon: '📝', label: '写下今日 3 件最重要的事', detail: '聚焦优先级，而非待办清单', type: 'step' },
    { icon: '🚀', label: '开始深度工作', detail: '利用早间黄金 2 小时', type: 'result' },
  ],
  conclusion: '坚持 21 天，晨间习惯就会变成自动驾驶模式',
  accentColor: '#6366f1',
};

export const LifeProcessFlow: React.FC<Partial<LifeProcessFlowProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = p.accentColor!;

  const titleProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });

  const nodeStart = 50;
  const nodeInterval = 55;
  const activeFloat = interpolate(frame, [nodeStart, nodeStart + p.nodes.length * nodeInterval], [0, p.nodes.length - 0.01], { extrapolateRight: 'clamp' });
  const activeNode = Math.floor(activeFloat);

  const conclusionStart = nodeStart + p.nodes.length * nodeInterval + 20;
  const conclusionOpacity = interpolate(frame, [conclusionStart, conclusionStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Node positions (centered vertically)
  const nodeSpacing = 195;
  const startY = 400;

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: '#1e293b',
        overflow: 'hidden',
      }}
    >
      {/* Subtle dot pattern */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle, #cbd5e130 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />

      {/* Title */}
      <div
        style={{
          position: 'absolute',
          top: 20,
          left: 60,
          right: 60,
          opacity: titleProgress,
          transform: `translateY(${(1 - titleProgress) * -15}px)`,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '8px 20px',
            background: `${accent}12`,
            borderRadius: 8,
            fontSize: 32,
            fontWeight: 600,
            color: accent,
            letterSpacing: '0.1em',
            marginBottom: 14,
          }}
        >
          FLOW GUIDE
        </div>
        <h1 style={{ fontSize: 58, fontWeight: 800, margin: 0, lineHeight: 1.25, letterSpacing: '-0.02em' }}>
          {p.topic}
        </h1>
        <p style={{ fontSize: 32, color: '#94a3b8', marginTop: 10, fontStyle: 'italic' }}>{p.subtitle}</p>
      </div>

      {/* ========== FLOW CHART SVG CONNECTIONS ========== */}
      <svg
        style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        viewBox="0 0 1080 1920"
      >
        {p.nodes.map((node, i) => {
          if (i >= p.nodes.length - 1) return null;
          const start = nodeStart + i * nodeInterval;
          const lineProgress = interpolate(frame, [start + 25, start + nodeInterval], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });

          const fromY = startY + i * nodeSpacing + 40;
          const toY = startY + (i + 1) * nodeSpacing - 10;
          const cx = node.type === 'decision' ? 540 : 540;

          // Decision branching arrows
          if (node.type === 'decision' && i + 2 < p.nodes.length) {
            return (
              <g key={i} opacity={lineProgress}>
                {/* Left branch */}
                <path
                  d={`M${cx - 30},${fromY} Q${cx - 100},${(fromY + toY) / 2} ${cx - 40},${toY}`}
                  fill="none"
                  stroke={accent}
                  strokeWidth="2"
                  strokeDasharray="400"
                  strokeDashoffset={400 * (1 - lineProgress)}
                  opacity="0.3"
                />
                {/* Right branch */}
                <path
                  d={`M${cx + 30},${fromY} Q${cx + 100},${(fromY + toY) / 2} ${cx + 40},${toY}`}
                  fill="none"
                  stroke={accent}
                  strokeWidth="2"
                  strokeDasharray="400"
                  strokeDashoffset={400 * (1 - lineProgress)}
                  opacity="0.3"
                />
              </g>
            );
          }

          return (
            <g key={i}>
              <line
                x1={cx}
                y1={fromY}
                x2={cx}
                y2={fromY + (toY - fromY) * lineProgress}
                stroke={accent}
                strokeWidth="2"
                opacity="0.25"
                strokeDasharray="6 4"
              />
              {/* Arrow */}
              {lineProgress > 0.9 && (
                <polygon
                  points={`${cx - 6},${toY - 6} ${cx + 6},${toY - 6} ${cx},${toY + 2}`}
                  fill={accent}
                  opacity="0.3"
                />
              )}
            </g>
          );
        })}
      </svg>

      {/* ========== FLOW NODES ========== */}
      <div
        style={{
          position: 'absolute',
          top: startY - 130,
          left: 60,
          right: 60,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {p.nodes.map((node, i) => {
          const start = nodeStart + i * nodeInterval;
          const nodeScale = spring({
            frame: frame - start,
            fps,
            from: 0,
            to: 1,
            config: { damping: 10, stiffness: 130, mass: 0.5 },
          });
          const nodeOpacity = interpolate(frame, [start, start + 10], [0, 1], { extrapolateRight: 'clamp' });
          const isActive = i === activeNode;
          const isPast = i < activeNode;

          // Pulse animation for active node
          const pulse = isActive ? interpolate(Math.sin(frame / 10), [-1, 1], [0.95, 1.05]) : 1;

          const isDecision = node.type === 'decision';
          const isResult = node.type === 'result';

          return (
            <div
              key={i}
              style={{
                width: isDecision ? 440 : 760,
                marginBottom: nodeSpacing - 120,
                opacity: nodeOpacity,
                transform: `scale(${nodeScale * pulse})`,
              }}
            >
              <div
                style={{
                  padding: isDecision ? '24px' : '24px 30px',
                  background: isResult
                    ? `linear-gradient(135deg, ${accent}15, rgba(16,185,129,0.08))`
                    : isActive
                      ? 'white'
                      : isPast
                        ? '#f8fafc'
                        : 'white',
                  borderRadius: isDecision ? 20 : 18,
                  border: isActive
                    ? `2px solid ${accent}`
                    : isResult
                      ? `2px solid ${accent}50`
                      : '1px solid #e2e8f0',
                  boxShadow: isActive
                    ? `0 8px 30px ${accent}20`
                    : '0 2px 8px rgba(0,0,0,0.04)',
                  transform: isDecision ? 'rotate(0deg)' : 'none',
                  textAlign: isDecision ? 'center' : 'left',
                  display: 'flex',
                  alignItems: isDecision ? 'center' : 'flex-start',
                  justifyContent: isDecision ? 'center' : 'flex-start',
                  gap: isDecision ? 10 : 16,
                  flexDirection: isDecision ? 'column' : 'row',
                  position: 'relative',
                }}
              >
                {/* Pulse ring for active */}
                {isActive && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: -6,
                      borderRadius: isDecision ? 24 : 22,
                      border: `2px solid ${accent}30`,
                      animation: 'none',
                    }}
                  />
                )}

                {/* Step number / icon */}
                <div
                  style={{
                    width: isDecision ? 56 : 66,
                    height: isDecision ? 56 : 66,
                    borderRadius: isDecision ? 14 : 18,
                    background: isResult ? accent : isPast ? '#10b981' : `${accent}12`,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: isDecision ? 30 : 36,
                    flexShrink: 0,
                    color: isResult || isPast ? 'white' : 'inherit',
                  }}
                >
                  {isPast && !isDecision && !isResult ? '✓' : node.icon}
                </div>

                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: isDecision ? 34 : 38,
                      fontWeight: 700,
                      color: isActive ? accent : isResult ? accent : '#1e293b',
                    }}
                  >
                    {node.label}
                  </div>
                  {node.detail && (
                    <div
                      style={{
                      fontSize: 26,
                      color: '#64748b',
                      marginTop: 6,
                        lineHeight: 1.4,
                        opacity: interpolate(frame, [start + 10, start + 25], [0, 1], { extrapolateRight: 'clamp' }),
                      }}
                    >
                      {node.detail}
                    </div>
                  )}
                </div>

                {/* Type badge */}
                {isDecision && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      padding: '4px 14px',
                      background: '#fbbf24',
                      borderRadius: 6,
                      fontSize: 18,
                      fontWeight: 700,
                      color: '#78350f',
                    }}
                  >
                    决策
                  </div>
                )}
                {isResult && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      padding: '4px 14px',
                      background: '#10b981',
                      borderRadius: 6,
                      fontSize: 18,
                      fontWeight: 700,
                      color: 'white',
                    }}
                  >
                    结果
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Conclusion */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 60,
          right: 60,
          textAlign: 'center',
          opacity: conclusionOpacity,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '20px 40px',
            background: accent,
            borderRadius: 50,
            color: 'white',
            fontSize: 32,
            fontWeight: 700,
            boxShadow: `0 8px 30px ${accent}30`,
          }}
        >
          💡 {p.conclusion}
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, conclusionStart + 40], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: accent,
        }}
      />
    </AbsoluteFill>
  );
};
